import { forwardRef, ButtonHTMLAttributes } from 'react';
import { clsx } from 'clsx';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', children, ...props }, ref) => {
    const baseClasses = 'inline-flex items-center justify-center font-body font-normal uppercase leading-relaxed tracking-extra-wide transition-all duration-300 focus:outline-none disabled:cursor-not-allowed';

    // Use semantic theme colors - CSS variables handle light/dark and partner theming
    const variants = {
      primary: 'bg-theme-accent text-theme-accent-text hover:opacity-90 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100',
      secondary: 'border border-theme-border-accent bg-transparent text-theme-text hover:bg-theme-accent hover:text-theme-accent-text disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-theme-text',
      ghost: 'text-theme-text hover:text-theme-text-secondary hover:scale-105 disabled:opacity-50 disabled:hover:scale-100',
      outline: 'border border-theme-border bg-transparent text-theme-text hover:bg-theme-accent hover:text-theme-accent-text hover:border-theme-border-accent disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-theme-text',
    };

    const sizes = {
      sm: 'px-4 py-2 text-xs',
      md: 'px-6 py-3 text-sm',
      lg: 'px-8 py-4 text-base',
    };

    return (
      <button
        className={clsx(
          baseClasses,
          variants[variant],
          sizes[size],
          className
        )}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };
